// Animated search placeholder for the shoplc.de header.
//
// A real `placeholder` attribute cannot be animated, so this hides the real placeholder and lays a
// text overlay over the input: a static prefix plus a masked box holding the rotating word. The
// overlay has pointer-events: none, so taps land on the input, and it clears the moment the field
// is focused or has text, so it never sits on a caret or fights with predictive search.
//
// The overlay is positioned from the input's OWN computed styles (font, letter spacing, line
// height, left padding, border width), so it lines up on both the mobile and the desktop bar
// without hardcoding measurements.
//
// Mobile (<= 1199px) rules, unchanged from v3:
// 1. The input's own `placeholder` stays empty on mobile, focused or not.
// 2. The rotating word uses the input's own font-weight (written inline, so it beats the CSS).
// 3. Font size is pinned to 14px on mobile; the measuring span is pinned with it.
//
// v4 - performance / smoothness, no behaviour change:
// * Layout is re-measured with a ResizeObserver on the input and its parent instead of on every
//   window `resize`. Mobile browsers fire `resize` while scrolling (URL bar collapsing), and the
//   old handler forced a synchronous layout per input on each of those - visible as jank while the
//   header was moving or the search popup was opening.
// * The rotation pauses while the input is not on screen (IntersectionObserver): the desktop bar
//   on mobile (display: none), the mobile bar on desktop, and the header while it is hidden on
//   scroll. Before, both bars animated all the time, so there was DOM churn every 2.2s on an
//   input nobody could see.
// * The placeholder attribute is only written when it actually changes.
// * Crossing the 1199px breakpoint is picked up with a matchMedia listener.
//
// v2 notes still apply: --in is removed before --out goes on (fixes stacked words), and a timeout
// removes the outgoing word regardless (covers prefers-reduced-motion, where animationend never
// fires). Settings are read from #slc-sph-config when present; the values below are fallbacks.

(() => {
  const FALLBACK = {
    prefix: 'Suche nach',
    words: ['Schmuck', 'Ringen', 'Ohrringen', 'Uhren', 'Edelsteinen', 'Halsketten', 'Armbändern'],
    hold: 2200,
    variant: 'scroll',
    scope: 'all',
    focusPlaceholder: 'Suche nach Schmuck, Uhren, Ringen …',
  };

  // Where the search inputs are. Each input is only set up once.
  const SELECTORS = [
    '.header-search-inline input[type="search"]',
    '.header-search-inline input[type="text"]',
    '.header-new__center input[type="search"]',
    '.header-new__mobile-search-wrapper input[type="search"]',
  ];

  // Longest animation in the stylesheet is 520ms. This only has to outlast it.
  const CLEANUP_AFTER = 900;

  // Mobile settings.
  const MOBILE_QUERY = '(max-width: 1199px)';
  const MOBILE_FONT_SIZE = 14;
  // What the input's real placeholder attribute holds on mobile. Empty on purpose.
  const MOBILE_PLACEHOLDER = '';

  const mobileMql = window.matchMedia(MOBILE_QUERY);
  const isMobile = () => mobileMql.matches;

  const readConfig = () => {
    const node = document.getElementById('slc-sph-config');
    if (!node) return FALLBACK;

    let parsed;
    try {
      parsed = JSON.parse(node.textContent);
    } catch (error) {
      return FALLBACK;
    }

    const words = String(parsed.words || '')
      .split(',')
      .map((word) => word.trim())
      .filter(Boolean);

    return {
      prefix: parsed.prefix || FALLBACK.prefix,
      words: words.length ? words : FALLBACK.words,
      hold: Number(parsed.hold) || FALLBACK.hold,
      variant: parsed.variant || FALLBACK.variant,
      scope: parsed.scope || FALLBACK.scope,
      focusPlaceholder: parsed.focusPlaceholder || FALLBACK.focusPlaceholder,
    };
  };

  const setup = (input, config) => {
    if (input.dataset.slcSphReady === '1') return;
    input.dataset.slcSphReady = '1';

    const parent = input.parentElement;
    if (!parent) return;

    // Only promote the parent to a positioning context if it is still static.
    if (getComputedStyle(parent).position === 'static') {
      parent.style.position = 'relative';
    }

    const overlay = document.createElement('span');
    overlay.className = 'slc-sph';
    overlay.setAttribute('aria-hidden', 'true');

    const staticPart = document.createElement('span');
    staticPart.className = 'slc-sph__static';
    staticPart.textContent = config.prefix;

    const rotator = document.createElement('span');
    rotator.className = 'slc-sph__rotator slc-sph__rotator--' + config.variant;

    overlay.appendChild(staticPart);
    overlay.appendChild(rotator);
    parent.appendChild(overlay);

    // Off-screen span used to measure each word so the rotator can size itself to fit.
    const measure = document.createElement('span');
    measure.className = 'slc-sph__measure';
    document.body.appendChild(measure);

    const realPlaceholder = input.getAttribute('placeholder') || '';

    let typography = {
      fontFamily: '',
      fontSize: '',
      fontWeight: '',
      letterSpacing: '',
      lineHeight: '',
    };

    const applyType = (node) => {
      node.style.fontFamily = typography.fontFamily;
      node.style.fontSize = typography.fontSize;
      node.style.fontWeight = typography.fontWeight;
      node.style.letterSpacing = typography.letterSpacing;
      node.style.lineHeight = typography.lineHeight;
    };

    const place = () => {
      // Not rendered (display: none on this breakpoint) - nothing to measure. The ResizeObserver
      // calls place() again as soon as the input gets a size.
      if (!input.offsetWidth) return;

      const cs = getComputedStyle(input);
      const mobile = isMobile();

      const fontSizePx = mobile ? MOBILE_FONT_SIZE : parseFloat(cs.fontSize) || 16;

      let lineHeight = parseFloat(cs.lineHeight);
      if (!lineHeight || Number.isNaN(lineHeight) || lineHeight < fontSizePx) {
        lineHeight = Math.round(fontSizePx * 1.2);
      }

      typography = {
        fontFamily: cs.fontFamily,
        fontSize: fontSizePx + 'px',
        fontWeight: mobile ? cs.fontWeight : '',
        letterSpacing: cs.letterSpacing,
        lineHeight: lineHeight + 'px',
      };

      applyType(overlay);
      applyType(rotator);
      applyType(measure);
      Array.from(rotator.children).forEach(applyType);

      const padLeft = parseFloat(cs.paddingLeft) || 0;
      const padRight = parseFloat(cs.paddingRight) || 0;
      const borderLeft = parseFloat(cs.borderLeftWidth) || 0;

      overlay.style.height = lineHeight + 'px';
      overlay.style.left = input.offsetLeft + borderLeft + padLeft + 'px';
      overlay.style.top = input.offsetTop + (input.offsetHeight - lineHeight) / 2 + 'px';
      overlay.style.maxWidth = Math.max(0, input.offsetWidth - padLeft - padRight) + 'px';

      rotator.style.height = lineHeight + 'px';
    };

    const widthOf = (text) => {
      measure.textContent = text;
      return Math.ceil(measure.getBoundingClientRect().width);
    };

    let index = 0;
    let timer = null;
    let onScreen = true; // corrected by the IntersectionObserver's first callback

    const fitRotator = () => {
      rotator.style.width = widthOf(config.words[index]) + 'px';
    };

    place();

    const first = document.createElement('span');
    first.className = 'slc-sph__word';
    first.textContent = config.words[0];
    applyType(first);
    rotator.appendChild(first);
    fitRotator();

    let current = first;

    const step = () => {
      index = (index + 1) % config.words.length;
      const next = config.words[index];

      // Safety net: only the outgoing and incoming words should ever be in the box at once.
      while (rotator.children.length > 2) {
        rotator.firstElementChild.remove();
      }

      rotator.style.width = widthOf(next) + 'px';

      const incoming = document.createElement('span');
      incoming.className = 'slc-sph__word slc-sph__word--in';
      incoming.textContent = next;
      applyType(incoming);
      rotator.appendChild(incoming);

      incoming.addEventListener(
        'animationend',
        () => incoming.classList.remove('slc-sph__word--in'),
        { once: true }
      );

      const outgoing = current;
      outgoing.classList.remove('slc-sph__word--in');
      outgoing.classList.add('slc-sph__word--out');

      const drop = () => outgoing.remove();
      outgoing.addEventListener('animationend', drop, { once: true });
      window.setTimeout(drop, CLEANUP_AFTER);

      current = incoming;
    };

    const start = () => {
      if (timer) return;
      timer = window.setInterval(step, config.hold);
    };

    const stop = () => {
      window.clearInterval(timer);
      timer = null;
    };

    const setPlaceholder = (value) => {
      if (input.getAttribute('placeholder') !== value) input.setAttribute('placeholder', value);
    };

    // The overlay only shows on an empty, unfocused field. On mobile the real placeholder stays
    // empty in every state; on desktop it takes over on focus.
    const sync = () => {
      const empty = input.value.trim() === '';
      const active = empty && document.activeElement !== input && !document.hidden;

      overlay.classList.toggle('slc-sph--hidden', !active);
      input.classList.toggle('slc-sph-input--masked', active);

      if (isMobile()) {
        setPlaceholder(MOBILE_PLACEHOLDER);
      } else {
        setPlaceholder(active ? realPlaceholder : config.focusPlaceholder);
      }

      if (active && onScreen) start();
      else stop();
    };

    input.addEventListener('focus', sync);
    input.addEventListener('blur', sync);
    input.addEventListener('input', sync);
    document.addEventListener('visibilitychange', sync);

    const relayout = () => {
      place();
      fitRotator();
    };

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(relayout);
      ro.observe(input);
      ro.observe(parent);
    } else {
      window.addEventListener('resize', relayout, { passive: true });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        onScreen = entries[entries.length - 1].isIntersecting;
        sync();
      }).observe(input);
    }

    mobileMql.addEventListener('change', () => {
      relayout();
      sync();
    });

    // Nunito Sans usually arrives after this runs; re-measure once it lands.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(relayout);
    }

    sync();
  };

  const init = () => {
    const config = readConfig();

    if (!config.words.length) return;
    if (config.scope === 'mobile' && !isMobile()) return;

    document.querySelectorAll(SELECTORS.join(',')).forEach((input) => setup(input, config));
  };

  // Perf: setup reads computed styles and layout (getComputedStyle, offsetWidth), which forces a
  // full layout while the page is still loading - ~0.5s of main-thread time on a mid-range phone.
  // It is cosmetic, so start it once the browser is idle (2s at the latest). Until then the
  // input's real placeholder shows.
  const scheduleInit = () => {
    if ('requestIdleCallback' in window) requestIdleCallback(init, { timeout: 2000 });
    else setTimeout(init, 1000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInit, { once: true });
  } else {
    scheduleInit();
  }
})();
